import {
	AutomixerInputDisengagedBehavior,
	ErrorIndicator,
	LightingColor,
	LightingMode,
	LightingState as ApiLightingState,
	LightingStyle,
	MicPolarPattern,
	MuteControlFunction,
	TransmitterLightingBehavior
} from '@shure/shared/angular/data-access/system-api/core';
import {
	DeviceMuteControlFunction,
	GateOffBehavior,
	LightBehavior,
	LightColor,
	LightingRingStyle,
	LightingState,
	PolarPattern,
	DeviceTransmitterLightingBehavior
} from '@shure/shared/angular/data-access/system-api/models';

import { LightErrorState } from '../../api/models/light-error-state';

export function mapLightingModeFromSysApi(mode?: LightingMode | null): LightBehavior {
	switch (mode) {
		case LightingMode.Flashing:
			return LightBehavior.Flashing;
		case LightingMode.Off:
			return LightBehavior.Off;
		case LightingMode.On:
			return LightBehavior.On;
		default:
			return LightBehavior.Off;
	}
}

export function mapLightingColorToSysApi(color?: LightColor): LightingColor {
	switch (color) {
		case LightColor.Blue:
			return LightingColor.Blue;
		case LightColor.Cyan:
			return LightingColor.Cyan;
		case LightColor.Gold:
			return LightingColor.Gold;
		case LightColor.Green:
			return LightingColor.Green;
		case LightColor.LightPurple:
			return LightingColor.LightPurple;
		case LightColor.Orange:
			return LightingColor.Orange;
		case LightColor.Orchid:
			return LightingColor.Orchid;
		case LightColor.Pink:
			return LightingColor.Pink;
		case LightColor.PowderBlue:
			return LightingColor.PowderBlue;
		case LightColor.Purple:
			return LightingColor.Purple;
		case LightColor.Red:
			return LightingColor.Red;
		case LightColor.SkyBlue:
			return LightingColor.SkyBlue;
		case LightColor.Turquoise:
			return LightingColor.Turquoise;
		case LightColor.Violet:
			return LightingColor.Violet;
		case LightColor.White:
			return LightingColor.White;
		case LightColor.Yellow:
			return LightingColor.Yellow;
		case LightColor.YellowGreen:
			return LightingColor.YellowGreen;
		default:
			return LightingColor.Green;
	}
}

export function mapLightingColorFromSysApi(color?: LightingColor | null): LightColor {
	switch (color) {
		case LightingColor.Blue:
			return LightColor.Blue;
		case LightingColor.Cyan:
			return LightColor.Cyan;
		case LightingColor.Gold:
			return LightColor.Gold;
		case LightingColor.Green:
			return LightColor.Green;
		case LightingColor.LightPurple:
			return LightColor.LightPurple;
		case LightingColor.Orange:
			return LightColor.Orange;
		case LightingColor.Orchid:
			return LightColor.Orchid;
		case LightingColor.Pink:
			return LightColor.Pink;
		case LightingColor.PowderBlue:
			return LightColor.PowderBlue;
		case LightingColor.Purple:
			return LightColor.Purple;
		case LightingColor.Red:
			return LightColor.Red;
		case LightingColor.SkyBlue:
			return LightColor.SkyBlue;
		case LightingColor.Turquoise:
			return LightColor.Turquoise;
		case LightingColor.Violet:
			return LightColor.Violet;
		case LightingColor.White:
			return LightColor.White;
		case LightingColor.Yellow:
			return LightColor.Yellow;
		case LightingColor.YellowGreen:
			return LightColor.YellowGreen;
		default:
			return LightColor.Green;
	}
}

export function mapLightingStateFromSysApi(state?: ApiLightingState | null): LightingState {
	switch (state) {
		case ApiLightingState.Muted:
			return LightingState.Muted;
		case ApiLightingState.Unmuted:
			return LightingState.Unmuted;
		default:
			return LightingState.Unmuted;
	}
}

export function mapLightRingStyleFromSysApi(style?: LightingStyle): LightingRingStyle {
	switch (style) {
		case LightingStyle.Segment:
			return LightingRingStyle.Segment;
		case LightingStyle.Ring:
			return LightingRingStyle.Ring;
		default:
			return LightingRingStyle.Ring;
	}
}

export function mapLightingAutomixGatingFromSysApi(behavior?: AutomixerInputDisengagedBehavior): GateOffBehavior {
	switch (behavior) {
		case AutomixerInputDisengagedBehavior.Off:
			return GateOffBehavior.Off;
		case AutomixerInputDisengagedBehavior.FollowMuteColor:
			return GateOffBehavior.FollowMuteColor;
		default:
			return GateOffBehavior.FollowMuteColor;
	}
}

export function mapMicPolarPatternFromSysApi(pattern?: MicPolarPattern): PolarPattern {
	switch (pattern) {
		case MicPolarPattern.Bidirectional:
			return PolarPattern.Bidirectional;
		case MicPolarPattern.Cardioid:
			return PolarPattern.Cardioid;
		case MicPolarPattern.Hypercardioid:
			return PolarPattern.Hypercardioid;
		case MicPolarPattern.Omnidirectional:
			return PolarPattern.Omnidirectional;
		case MicPolarPattern.Supercardioid:
			return PolarPattern.Supercardioid;
		case MicPolarPattern.Toroid:
			return PolarPattern.Toroid;
		default:
			return PolarPattern.Cardioid;
	}
}

export function mapAudioMuteControlFunctionFromSysApi(
	muteControlFunction?: MuteControlFunction
): DeviceMuteControlFunction {
	switch (muteControlFunction) {
		case MuteControlFunction.Disabled:
			return DeviceMuteControlFunction.Disabled;
		case MuteControlFunction.Local:
			return DeviceMuteControlFunction.Local;
		case MuteControlFunction.LogicOut:
			return DeviceMuteControlFunction.LogicOut;
		default:
			return DeviceMuteControlFunction.Disabled;
	}
}

export function mapTransmitterLightingBehaviorFromSysApi(
	transmitterLightingBehavior?: TransmitterLightingBehavior
): DeviceTransmitterLightingBehavior {
	switch (transmitterLightingBehavior) {
		case TransmitterLightingBehavior.UnmuteSolidGreenMuteSolidRed:
			return DeviceTransmitterLightingBehavior.UnmuteSolidGreenMuteSolidRed;
		case TransmitterLightingBehavior.UnmuteSolidRedMuteFlashingRed:
			return DeviceTransmitterLightingBehavior.UnmuteSolidRedMuteFlashingRed;
		case TransmitterLightingBehavior.UnmuteSolidRedMuteOff:
			return DeviceTransmitterLightingBehavior.UnmuteSolidRedMuteOff;
		case TransmitterLightingBehavior.ExternalControl:
			return DeviceTransmitterLightingBehavior.ExternalControl;
		default:
			return DeviceTransmitterLightingBehavior.UnmuteSolidGreenMuteSolidRed;
	}
}

export function mapTransmitterDeviceLightingBehaviorToSysApi(
	transmitterDeviceLightingBehavior?: DeviceTransmitterLightingBehavior
): TransmitterLightingBehavior {
	switch (transmitterDeviceLightingBehavior) {
		case DeviceTransmitterLightingBehavior.ExternalControl:
			return TransmitterLightingBehavior.ExternalControl;
		case DeviceTransmitterLightingBehavior.UnmuteSolidGreenMuteSolidRed:
			return TransmitterLightingBehavior.UnmuteSolidGreenMuteSolidRed;
		case DeviceTransmitterLightingBehavior.UnmuteSolidRedMuteFlashingRed:
			return TransmitterLightingBehavior.UnmuteSolidRedMuteFlashingRed;
		case DeviceTransmitterLightingBehavior.UnmuteSolidRedMuteOff:
			return TransmitterLightingBehavior.UnmuteSolidRedMuteOff;
		default:
			return TransmitterLightingBehavior.UnmuteSolidGreenMuteSolidRed;
	}
}

export function mapErrorIndicatorStateFromSysApi(state?: ErrorIndicator): LightErrorState {
	switch (state) {
		case ErrorIndicator.ClearableError:
			return LightErrorState.ClearableError;
		case ErrorIndicator.NonClearableError:
			return LightErrorState.NonClearableError;
		default:
			return LightErrorState.NoError;
	}
}
